import React, { useState, useEffect, useCallback } from 'react';
import { ICON } from '../icons/iconIds';
import IconText from './icons/IconText';
import Layout from './layout/Layout';
import Card from './ui/Card';
import Button from './ui/Button';
import { docusignService } from '../services/docusignService';
import './DocuSignConnect.css';

function envelopeRowsFromApi(body) {
  const data = body?.data;
  if (!data || typeof data !== 'object') return [];
  const list = data.envelopes || data.envelopeSummaries || data.folderItems;
  if (!Array.isArray(list)) return [];
  return list;
}

const DocuSignConnect = ({ user }) => {
  const [meLoading, setMeLoading] = useState(false);
  const [meResult, setMeResult] = useState(null);
  const [envLoading, setEnvLoading] = useState(false);
  const [envResult, setEnvResult] = useState(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const [debugResult, setDebugResult] = useState(null);
  const [banner, setBanner] = useState(null);

  const refreshMe = useCallback(async () => {
    setMeLoading(true);
    setBanner(null);
    try {
      const res = await docusignService.fetchMe();
      setMeResult(res);
      if (!res.body?.success) {
        setBanner(
          res.body?.error ||
            `DocuSign /me failed (${res.status}). Configure Function App DocuSign settings or complete OAuth.`
        );
      }
    } catch (e) {
      setMeResult({ ok: false, body: { success: false, error: e.message } });
      setBanner(e.message);
    } finally {
      setMeLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  const serverConnected = Boolean(meResult?.body?.success);

  const startOAuth = () => {
    window.location.assign(docusignService.getOAuthStartUrl());
  };

  const loadEnvelopes = async () => {
    setEnvLoading(true);
    setBanner(null);
    try {
      const docusignUserId = (meResult?.body?.userinfo?.sub || '').trim();
      const query = { from_date: '2020-01-01' };
      if (docusignUserId) {
        query.user_id = docusignUserId;
      }
      const res = await docusignService.fetchEnvelopes(query);
      setEnvResult(res);
      if (!res.body?.success) {
        setBanner(res.body?.error || `Envelopes request failed (${res.status}).`);
      } else if (!docusignUserId) {
        setBanner(
          'No DocuSign user id from /me yet — listing used server DOCUSIGN_API_USER_ID only. Run “Check connection” first for the signed-in DocuSign user.'
        );
      }
    } catch (e) {
      setEnvResult({ ok: false, body: { success: false, error: e.message } });
      setBanner(e.message);
    } finally {
      setEnvLoading(false);
    }
  };

  const loadDebug = async () => {
    setDebugLoading(true);
    setBanner(null);
    try {
      const res = await docusignService.fetchDebugBatch();
      setDebugResult(res);
      if (!res.body?.success) {
        setBanner(res.body?.error || `Debug batch failed (${res.status}).`);
      }
    } catch (e) {
      setDebugResult({ ok: false, body: { success: false, error: e.message } });
      setBanner(e.message);
    } finally {
      setDebugLoading(false);
    }
  };

  const envelopeRows = envelopeRowsFromApi(envResult?.body);

  return (
    <Layout user={user} currentRoute="/docusign">
      <div className="debug docusign-connect">
        <div className="debug-header">
          <IconText icon={ICON.appDocusign} as="h1">DocuSign</IconText>
          <p>
            Connect the Function App to production DocuSign, then load envelopes for the DocuSign
            user tied to the server token (<code className="kv-check-code">/me</code> →{' '}
            <code className="kv-check-code">sub</code>). OAuth stores the refresh token in Azure.
          </p>
        </div>

        <Card className="kv-check-card">
          <h2 className="kv-check-title">Connection</h2>
          <p className="kv-check-desc">
            <code className="kv-check-code">GET /api/docusign/me</code> uses the server{' '}
            <code className="kv-check-code">DOCUSIGN_REFRESH_TOKEN</code>. If it fails, use{' '}
            <strong>Sign in with DocuSign</strong>, then copy <code className="kv-check-code">
              refresh_token
            </code>{' '}
            and suggested account settings from the callback JSON into the Function App configuration.
          </p>
          <div className="docusign-status-row">
            <span
              className={`docusign-status-badge ${serverConnected ? 'docusign-status-ok' : 'docusign-status-bad'}`}
            >
              {serverConnected ? 'Server token OK' : 'No server token / misconfigured'}
            </span>
            <span className="docusign-user-hint">
              Signed in as {user?.name || user?.username || '—'} (Microsoft); DocuSign user comes
              from /me below.
            </span>
          </div>
          <div className="kv-check-toolbar">
            <Button type="button" onClick={startOAuth} variant="primary">
              Sign in with DocuSign
            </Button>
            <Button type="button" onClick={refreshMe} loading={meLoading} variant="secondary">
              Check connection
            </Button>
            <Button type="button" onClick={loadEnvelopes} loading={envLoading} variant="secondary">
              Load envelopes
            </Button>
            <Button type="button" onClick={loadDebug} loading={debugLoading} variant="secondary">
              Debug batch
            </Button>
          </div>
          {banner && (
            <p className="kv-check-error" role="alert">
              {banner}
            </p>
          )}
        </Card>

        <Card className="kv-check-card">
          <h2 className="kv-check-title">User info (JSON)</h2>
          <div className="kv-check-result-wrap">
            <textarea
              className="kv-check-result"
              readOnly
              rows={14}
              value={meResult ? JSON.stringify(meResult.body, null, 2) : ''}
              placeholder="Run “Check connection”…"
            />
          </div>
        </Card>

        {envelopeRows.length > 0 && (
          <Card className="kv-check-card">
            <h2 className="kv-check-title">Envelopes (summary table)</h2>
            <div className="docusign-table-wrap">
              <table className="docusign-table">
                <thead>
                  <tr>
                    <th>Subject</th>
                    <th>Status</th>
                    <th>Envelope ID</th>
                    <th>Sent</th>
                  </tr>
                </thead>
                <tbody>
                  {envelopeRows.slice(0, 50).map((row) => (
                    <tr key={row.envelopeId || row.uri || JSON.stringify(row)}>
                      <td>{row.emailSubject || row.subject || '—'}</td>
                      <td>{row.status || '—'}</td>
                      <td className="docusign-mono">{row.envelopeId || '—'}</td>
                      <td>{row.sentDateTime || row.createdDateTime || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {envelopeRows.length > 50 && (
                <p className="kv-check-desc">Showing first 50 of {envelopeRows.length} rows.</p>
              )}
            </div>
          </Card>
        )}

        <Card className="kv-check-card">
          <h2 className="kv-check-title">Envelopes (raw JSON)</h2>
          <div className="kv-check-result-wrap">
            <textarea
              className="kv-check-result"
              readOnly
              rows={16}
              value={envResult ? JSON.stringify(envResult.body, null, 2) : ''}
              placeholder="Click “Load envelopes” (current DocuSign user from /me, from_date=2020-01-01; not account-wide)."
            />
          </div>
        </Card>

        <Card className="kv-check-card">
          <h2 className="kv-check-title">Debug batch (JSON)</h2>
          <p className="kv-check-desc">
            Folders + search_folders variants + plain envelopes — useful when the default list is
            empty.
          </p>
          <div className="kv-check-result-wrap">
            <textarea
              className="kv-check-result"
              readOnly
              rows={12}
              value={debugResult ? JSON.stringify(debugResult.body, null, 2) : ''}
              placeholder='Optional: “Debug batch”.'
            />
          </div>
        </Card>
      </div>
    </Layout>
  );
};

export default DocuSignConnect;
